import React, { useState, useEffect, useCallback } from 'react';
import WhatsAppSettings from './WhatsAppSettings';
import DualCalendarPicker from './DualCalendarPicker';
import {
  getOrgDepartments, createOrgDepartment, updateOrgDepartment, deleteOrgDepartment,
  getDeptChannelDefaults, getWhatsAppIntegrations, updateDeptChannelDefaults,
  getWhatsAppBusinessSettings, updateWhatsAppBusinessSettings,
  getOrganizationBranding, updateOrganizationBranding,
} from '../utils/api';
import { useToast } from './Toast';
import { FaSitemap, FaEdit, FaTrash, FaPlus, FaCheck, FaTimes, FaWhatsapp, FaLockOpen, FaBuilding, FaLink, FaImage } from 'react-icons/fa';
import IntakeTab from './IntakeSettings';

// ─── Sidebar tab definitions (general → specific) ──────────────────────────────
const TABS = [
  { key: 'organization',  icon: FaBuilding,  label: 'Organization', label_ar: 'المؤسسة'      },
  { key: 'departments',   icon: FaSitemap,   label: 'Teams',        label_ar: 'الفرق'        },
  { key: 'whatsapp',      icon: FaWhatsapp,  label: 'WhatsApp',     label_ar: 'واتساب'       },
  { key: 'intake',       icon: FaLink,      label: 'Website intake', label_ar: 'استقبال الموقع' },
];

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle = {
  padding: '6px 10px', borderRadius: '8px', border: '1px solid #cbd5e1',
  fontSize: '0.82rem', color: '#0f172a', background: '#fff', outline: 'none',
};
const iconBtnStyle = (color) => ({
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: '28px', height: '28px', borderRadius: '6px', border: 'none',
  background: `${color}18`, color, cursor: 'pointer', fontSize: '12px', flexShrink: 0,
});
const sectionHeadStyle = {
  margin: '32px 0 16px',
  paddingBottom: '10px',
  borderBottom: '2px solid #f97316',
  fontSize: '0.875rem',
  fontWeight: 700,
  color: '#ea580c',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};
const cardStyle = {
  maxWidth: '860px',
  background: '#fff',
  border: '1px solid #e2e8f0', 
  borderRadius: '14px',
  padding: '20px',
  marginBottom: '16px',
};

// ─── Global dept list ─────────────────────────────────────────────────────────
const GLOBAL_DEPT_VALUES = [
  'marketing','client_support','sales','administration','leasing',
  'property_management','finance','collections','maintenance','contracts','advisory','valuation',
];

const GLOBAL_DEPTS_AR = {
  marketing: 'التسويق', client_support: 'دعم العملاء', sales: 'المبيعات',
  administration: 'الإدارة', leasing: 'التأجير', property_management: 'إدارة الأملاك',
  finance: 'المالية', collections: 'التحصيل', maintenance: 'الصيانة',
  contracts: 'العقود', advisory: 'الاستشارات', valuation: 'التقييم',
};

/** Simple Arabic to English transliteration for creating stable DB keys */
const transliterateArabic = (arabicText) => {
  const map = {
    'ا': 'a', 'أ': 'a', 'آ': 'aa', 'إ': 'i', 'ب': 'b', 'ت': 't', 'ث': 'th', 'ج': 'j', 'ح': 'h', 'خ': 'kh',
    'د': 'd', 'ذ': 'dh', 'ر': 'r', 'ز': 'z', 'س': 's', 'ش': 'sh', 'ص': 's', 'ض': 'd', 'ط': 't', 'ظ': 'z',
    'ع': 'a', 'غ': 'gh', 'ف': 'f', 'ق': 'q', 'ك': 'k', 'ل': 'l', 'م': 'm', 'ن': 'n', 'ه': 'h', 'و': 'w',
    'ي': 'y', 'ى': 'a', 'ة': 'a', 'ء': '', 'ئ': 'e', 'ؤ': 'o', 'لا': 'la'
  };
  
  return String(arabicText || '')
    .trim()
    .split('')
    .map(char => map[char] || (char.match(/[a-z0-9]/i) ? char.toLowerCase() : ''))
    .join('')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || `team_${Date.now().toString(36)}`;
};

/** Generate stable team key from Arabic name */
const generateTeamKey = (arabicName) => {
  const transliterated = transliterateArabic(arabicName);
  return transliterated || `team_${Date.now().toString(36)}`;
};

/** Check for similar team names (exact match, transliteration conflict, meaning overlap) */
const validateTeamName = (newArabicName, existingCustomTeams, language) => {
  const newName = newArabicName.trim();
  const newKey = generateTeamKey(newName);
  
  // Check against global departments
  if (GLOBAL_DEPT_VALUES.includes(newKey)) {
    const globalName = language === 'ar' ? GLOBAL_DEPTS_AR[newKey] : newKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return {
      isValid: false,
      error: language === 'ar' 
        ? `هذا الاسم يطابق الفريق الأساسي "${globalName}" — استخدم اسماً مختلفاً`
        : `This name matches the standard team "${globalName}" — use a different name`
    };
  }
  
  // Check exact Arabic name duplicates
  const exactMatch = existingCustomTeams.find(t => t.label_ar && t.label_ar.trim().toLowerCase() === newName.toLowerCase());
  if (exactMatch) {
    return {
      isValid: false,
      error: language === 'ar' 
        ? 'هذا الاسم مستخدم بالفعل'
        : 'This name is already used'
    };
  }
  
  // Check transliteration key conflicts
  const keyConflict = existingCustomTeams.find(t => generateTeamKey(t.label_ar || t.label) === newKey);
  if (keyConflict) {
    return {
      isValid: false,
      error: language === 'ar' 
        ? `هذا الاسم مشابه جداً لـ "${keyConflict.label_ar || keyConflict.label}" — استخدم اسماً مختلفاً`
        : `This name is too similar to "${keyConflict.label_ar || keyConflict.label}" — use a different name`
    };
  }
  
  // Check for similar meanings (basic synonyms)
  const arabicSynonyms = {
    'مبيعات': ['بيع', 'تسويق', 'مبيعات'],
    'التسويق': ['مبيعات', 'ترويج', 'إعلان', 'تسويق'],
    'دعم العملاء': ['خدمة العملاء', 'دعم', 'خدمات', 'عملاء'],
    'المالية': ['محاسبة', 'مالية', 'حسابات', 'مال'],
    'إدارة الأملاك': ['أملاك', 'عقارات', 'ممتلكات', 'إدارة عقارية'],
    'صيانة': ['إصلاح', 'ترميم', 'صيانة'],
    'عقود': ['اتفاقيات', 'تعاقد', 'قانونية']
  };
  
  for (const [, synonyms] of Object.entries(arabicSynonyms)) {
    if (synonyms.some(synonym => newName.includes(synonym))) {
      const conflictingTeam = existingCustomTeams.find(t => 
        t.label_ar && synonyms.some(synonym => t.label_ar.includes(synonym))
      ) || (GLOBAL_DEPT_VALUES.find(globalKey => 
        synonyms.some(synonym => (GLOBAL_DEPTS_AR[globalKey] || '').includes(synonym))
      ) ? { label_ar: GLOBAL_DEPTS_AR[GLOBAL_DEPT_VALUES.find(gk => 
        synonyms.some(synonym => (GLOBAL_DEPTS_AR[gk] || '').includes(synonym))
      )] } : null);
      
      if (conflictingTeam) {
        return {
          isValid: false,
          error: language === 'ar' 
            ? `هذا الاسم له معنى مشابه لـ "${conflictingTeam.label_ar}" — استخدم اسماً أكثر تحديداً`
            : `This name has similar meaning to "${conflictingTeam.label_ar}" — use a more specific name`
        };
      }
    }
  }
  
  return { isValid: true };
};

// ─── Number Access Section ────────────────────────────────────────────────────
// Per-WABA card: which departments have access by default.
// WABA-centric (1–3 cards) is far more readable than dept-centric (12+ rows).
const NumberAccessSection = ({ language }) => {
  const toast = useToast();
  const [integrations, setIntegrations] = useState([]);
  const [customDepts, setCustomDepts] = useState([]);
  // { [phone_number_id]: Set<dept_value> }
  const [accessMap, setAccessMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isRTL = language === 'ar';

  const allDepts = useCallback(() => [
    ...GLOBAL_DEPT_VALUES.map(v => ({
      value: v,
      label: language === 'ar' ? (GLOBAL_DEPTS_AR[v] || v) : v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    })),
    ...customDepts
      .filter(c => !GLOBAL_DEPT_VALUES.includes(c.value))
      .map(c => ({ value: c.value, label: language === 'ar' && c.label_ar ? c.label_ar : c.label })),
  ], [language, customDepts]);

  useEffect(() => {
    Promise.all([getWhatsAppIntegrations(), getDeptChannelDefaults(), getOrgDepartments()])
      .then(([ir, dr, ordr]) => {
        const intgs = Array.isArray(ir.data?.integrations) ? ir.data.integrations : [];
        setIntegrations(intgs);
        setCustomDepts(Array.isArray(ordr.data?.departments) ? ordr.data.departments : []);

        // Build accessMap: { phone_number_id: [dept_value, ...] }
        // Source data is { dept_value: [phone_number_id, ...] }, invert it
        const rows = Array.isArray(dr.data?.defaults) ? dr.data.defaults : [];
        const map = {};
        intgs.forEach(i => { map[String(i.phone_number_id)] = new Set(); });
        rows.filter(r => r.channel_type === 'whatsapp').forEach(r => {
          if (map[r.channel_identifier]) map[r.channel_identifier].add(r.department_value);
        });
        setAccessMap(map);
      })
      .catch(() => toast.error(language === 'ar' ? 'تعذر التحميل' : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (phoneId, deptValue) => {
    setAccessMap(prev => {
      const next = new Set(prev[phoneId] || []);
      next.has(deptValue) ? next.delete(deptValue) : next.add(deptValue);
      return { ...prev, [phoneId]: next };
    });
  };

  const toggleAll = (phoneId, selectAll) => {
    setAccessMap(prev => ({
      ...prev,
      [phoneId]: selectAll ? new Set(allDepts().map(d => d.value)) : new Set(),
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Convert accessMap { phone_id → Set<dept> } → flat entries { dept → phone_id }
      const entries = [];
      Object.entries(accessMap).forEach(([phoneId, depts]) => {
        depts.forEach(dept => entries.push({
          department_value: dept,
          channel_type: 'whatsapp',
          channel_identifier: phoneId,
          access_level: 'write',
        }));
      });
      await updateDeptChannelDefaults(entries);
      toast.success(language === 'ar' ? 'تم حفظ صلاحيات الأرقام' : 'Access saved');
    } catch { toast.error(language === 'ar' ? 'تعذر الحفظ' : 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading…'}</p>;
  if (!integrations.length) return (
    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
      {language === 'ar' ? 'لا توجد أرقام متصلة بعد — أضف رقماً من قسم التكاملات أعلاه.' : 'No connected numbers yet — add one in Integrations above.'}
    </p>
  );

  const depts = allDepts();

  return (
    <div>
      {/* One card per connected number */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {integrations.map(intg => {
          const phoneId = String(intg.phone_number_id || '');
          const granted = accessMap[phoneId] || new Set();
          const allSelected = depts.every(d => granted.has(d.value));
          const noneSelected = depts.every(d => !granted.has(d.value));

          return (
            <div key={phoneId} style={{ borderRadius: '12px', border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden' }}>
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg,#25d366,#128c7e)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <FaWhatsapp style={{ color: '#fff', fontSize: '15px' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {intg.business_display_name || phoneId}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>{phoneId}</p>
                </div>
                {/* Quick select all / none */}
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button onClick={() => toggleAll(phoneId, true)} disabled={allSelected}
                    style={{ padding: '3px 9px', borderRadius: '6px', border: '1px solid #d1fae5', background: allSelected ? '#f0fdf4' : '#fff', color: '#059669', fontSize: '0.72rem', fontWeight: 700, cursor: allSelected ? 'default' : 'pointer', opacity: allSelected ? 0.5 : 1 }}>
                    {language === 'ar' ? 'الكل' : 'All'}
                  </button>
                  <button onClick={() => toggleAll(phoneId, false)} disabled={noneSelected}
                    style={{ padding: '3px 9px', borderRadius: '6px', border: '1px solid #fee2e2', background: noneSelected ? '#fef2f2' : '#fff', color: '#dc2626', fontSize: '0.72rem', fontWeight: 700, cursor: noneSelected ? 'default' : 'pointer', opacity: noneSelected ? 0.5 : 1 }}>
                    {language === 'ar' ? 'لا شيء' : 'None'}
                  </button>
                </div>
              </div>

              {/* Dept pill toggles */}
              <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
                {depts.map(dept => {
                  const active = granted.has(dept.value);
                  return (
                    <button key={dept.value} onClick={() => toggle(phoneId, dept.value)}
                      style={{
                        padding: '5px 11px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                        background: active ? '#dcfce7' : '#f1f5f9',
                        color: active ? '#166534' : '#475569',
                        fontWeight: active ? 700 : 500,
                        fontSize: '0.8rem',
                        transition: 'background 0.12s, color 0.12s',
                        outline: active ? '2px solid #86efac' : '2px solid transparent',
                      }}
                    >
                      {active && <span style={{ marginInlineEnd: '4px', fontSize: '0.65rem' }}>✓</span>}
                      {dept.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={handleSave} disabled={saving}
        style={{ marginTop: '16px', padding: '9px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#059669,#10b981)', color: '#fff', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer' }}>
        {saving ? '...' : (language === 'ar' ? 'حفظ صلاحيات الأرقام' : 'Save number access')}
      </button>
    </div>
  );
};

// ─── Organization branding (logo + business name) ─────────────────────────────
const OrgBrandingTab = ({ language, onBrandingSaved }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getOrganizationBranding();
      setBusinessName(res.data?.business_name || '');
      setLogoUrl(res.data?.company_logo_url || null);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 1_800_000) {
      toast?.error?.(language === 'ar' ? 'الشعار كبير جداً' : 'Logo too large');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateOrganizationBranding({
        business_name: businessName,
        company_logo_url: logoUrl,
      });
      toast?.success?.(language === 'ar' ? 'تم حفظ هوية المؤسسة' : 'Branding saved');
      onBrandingSaved?.(res.data);
    } catch (e) {
      toast?.error?.(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p style={{ color: '#94a3b8' }}>…</p>;
  }

  return (
    <div style={{ marginBottom: 28, padding: 20, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '1rem', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
        <FaImage style={{ color: '#ea580c' }} />
        {language === 'ar' ? 'هوية المؤسسة' : 'Company branding'}
      </h3>
      <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.85rem' }}>
        {language === 'ar'
          ? 'شعار واسم الشركة يظهران في ريل وفي نموذج تسجيل الفعاليات.'
          : 'Logo and company name appear in Real and on event registration forms.'}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 16, alignItems: 'start' }}>
        <div
          style={{
            width: 120,
            height: 120,
            borderRadius: 12,
            border: '1px dashed #cbd5e1',
            background: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {logoUrl ? (
            <img src={logoUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <span style={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center', padding: 8 }}>
              {language === 'ar' ? 'لا شعار' : 'No logo'}
            </span>
          )}
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            {language === 'ar' ? 'اسم المؤسسة' : 'Business name'}
            <input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              style={{
                display: 'block',
                width: '100%',
                marginTop: 4,
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #cbd5e1',
                boxSizing: 'border-box',
              }}
            />
          </label>
          <label style={{ fontSize: '0.8rem', color: '#475569' }}>
            {language === 'ar' ? 'رفع الشعار' : 'Upload logo'}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => onFile(e.target.files?.[0])}
              style={{ display: 'block', marginTop: 6, fontSize: '0.8rem' }}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: '#ea580c',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saving ? '…' : language === 'ar' ? 'حفظ' : 'Save'}
            </button>
            {logoUrl && (
              <button
                type="button"
                onClick={() => setLogoUrl(null)}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid #fecaca',
                  background: '#fff',
                  color: '#b91c1c',
                  cursor: 'pointer',
                }}
              >
                {language === 'ar' ? 'إزالة الشعار' : 'Remove logo'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Business Hours tab ───────────────────────────────────────────────────────
const BusinessHoursTab = ({ language }) => {
  const toast = useToast();
  const [bsLoading, setBsLoading] = useState(false);
  const [bsSaving, setBsSaving] = useState(false);
  const [bsWorkingDays, setBsWorkingDays] = useState([0, 1, 2, 3, 4, 5]); // 0=Sun … 6=Sat
  const [bsWorkStart, setBsWorkStart] = useState('09:00');
  const [bsWorkEnd, setBsWorkEnd] = useState('22:00');
  const [bsDayHours, setBsDayHours] = useState({}); // {0: {start: '09:00', end: '18:00'}, 5: {...}}
  const [bsTimezone, setBsTimezone] = useState('Asia/Riyadh');
  const [bsHolidays, setBsHolidays] = useState([]); // [{date:'YYYY-MM-DD', label:'...'}]
  const [bsNewHolidayDate, setBsNewHolidayDate] = useState('');
  const [bsNewHolidayLabel, setBsNewHolidayLabel] = useState('');

  const loadBusinessSettings = async () => {
    try {
      setBsLoading(true);
      const res = await getWhatsAppBusinessSettings();
      const d = res.data || {};
      setBsWorkingDays(Array.isArray(d.working_days) ? d.working_days : [0, 1, 2, 3, 4, 5]);
      setBsWorkStart(d.work_start || '09:00');
      setBsWorkEnd(d.work_end || '22:00');
      setBsDayHours(d.day_hours && typeof d.day_hours === 'object' ? d.day_hours : {});
      setBsTimezone(d.timezone || 'Asia/Riyadh');
      setBsHolidays(Array.isArray(d.holidays) ? d.holidays : []);
    } catch (err) {
      console.error('Failed to load business settings:', err);
    } finally {
      setBsLoading(false);
    }
  };

  const handleSaveBusinessSettings = async () => {
    // Validate default hours
    if (bsWorkStart >= bsWorkEnd) {
      toast.error(language === 'ar' ? 'وقت البداية يجب أن يكون قبل وقت النهاية' : 'Start time must be before end time');
      return;
    }
    
    // Validate per-day hours
    for (const [day, hours] of Object.entries(bsDayHours)) {
      if (hours && hours.start && hours.end && hours.start >= hours.end) {
        const dayNames = { 
          0: language === 'ar' ? 'الأحد' : 'Sunday', 
          1: language === 'ar' ? 'الاثنين' : 'Monday', 
          2: language === 'ar' ? 'الثلاثاء' : 'Tuesday',
          3: language === 'ar' ? 'الأربعاء' : 'Wednesday',
          4: language === 'ar' ? 'الخميس' : 'Thursday',
          5: language === 'ar' ? 'الجمعة' : 'Friday',
          6: language === 'ar' ? 'السبت' : 'Saturday'
        };
        toast.error(`${dayNames[day]}: ${language === 'ar' ? 'وقت البداية يجب أن يكون قبل وقت النهاية' : 'Start time must be before end time'}`);
        return;
      }
    }

    try {
      setBsSaving(true);
      // Only send non-empty day_hours
      const cleanDayHours = Object.fromEntries(
        Object.entries(bsDayHours).filter(([_, hours]) => 
          hours && hours.start && hours.end && hours.start !== hours.end
        )
      );
      
      await updateWhatsAppBusinessSettings({
        working_days: bsWorkingDays,
        work_start: bsWorkStart,
        work_end: bsWorkEnd,
        day_hours: Object.keys(cleanDayHours).length > 0 ? cleanDayHours : null,
        timezone: bsTimezone,
        holidays: bsHolidays
      });
      toast.success(language === 'ar' ? 'تم حفظ إعدادات العمل' : 'Business settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.error || (language === 'ar' ? 'تعذر الحفظ' : 'Save failed'));
    } finally {
      setBsSaving(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setBsWorkingDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)
    );
  };

  const addHoliday = () => {
    if (!bsNewHolidayDate) return;
    if (bsHolidays.some((h) => h.date === bsNewHolidayDate)) return;
    setBsHolidays((prev) => [...prev, { date: bsNewHolidayDate, label: bsNewHolidayLabel.trim() || bsNewHolidayDate }].sort((a, b) => a.date.localeCompare(b.date)));
    setBsNewHolidayDate('');
    setBsNewHolidayLabel('');
  };

  const removeHoliday = (date) => setBsHolidays((prev) => prev.filter((h) => h.date !== date));

  // Per-day hours helpers
  const setDayHours = (day, start, end) => {
    setBsDayHours(prev => ({
      ...prev,
      [day]: start && end ? { start, end } : undefined
    }));
  };

  const clearDayHours = (day) => {
    setBsDayHours(prev => {
      const next = { ...prev };
      delete next[day];
      return next;
    });
  };

  const getDayHours = (day) => {
    return bsDayHours[day] || { start: bsWorkStart, end: bsWorkEnd };
  };

  useEffect(() => {
    loadBusinessSettings();
  }, []);

  return (
    <div style={cardStyle}>
      <h2 style={{ margin: '0 0 4px 0', color: '#0f172a', fontSize: '18px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
        🗓️ {language === 'ar' ? 'إعدادات أوقات العمل' : 'Business Hours Settings'}
      </h2>
      <p style={{ margin: '0 0 18px 0', color: '#64748b', fontSize: '13px', lineHeight: 1.6 }}>
        {language === 'ar'
          ? 'تُستخدم هذه الإعدادات لاحقاً في احتساب أداء الفريق واستجابة الغياب التلقائية خارج أوقات العمل.'
          : 'These settings will be used to compute team performance and power automated out-of-hours replies.'}
      </p>

      {bsLoading ? (
        <div style={{ color: '#64748b', fontSize: '13px' }}>{language === 'ar' ? 'جاري التحميل…' : 'Loading…'}</div>
      ) : (
        <>
          {/* Working days */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
              {language === 'ar' ? 'أيام العمل' : 'Working Days'}
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {[
                { day: 0, arLabel: 'الأحد', enLabel: 'Sun' },
                { day: 1, arLabel: 'الاثنين', enLabel: 'Mon' },
                { day: 2, arLabel: 'الثلاثاء', enLabel: 'Tue' },
                { day: 3, arLabel: 'الأربعاء', enLabel: 'Wed' },
                { day: 4, arLabel: 'الخميس', enLabel: 'Thu' },
                { day: 5, arLabel: 'الجمعة', enLabel: 'Fri' },
                { day: 6, arLabel: 'السبت', enLabel: 'Sat' }
              ].map(({ day, arLabel, enLabel }) => {
                const active = bsWorkingDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleWorkingDay(day)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      border: `1.5px solid ${active ? '#f97316' : '#e2e8f0'}`,
                      background: active ? '#fff7ed' : '#f8fafc',
                      color: active ? '#ea580c' : '#64748b',
                      fontWeight: active ? 700 : 400,
                      fontSize: '13px',
                      cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                  >
                    {language === 'ar' ? arLabel : enLabel}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Default working hours */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
              {language === 'ar' ? 'ساعات العمل الافتراضية' : 'Default Working Hours'}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#64748b' }}>
                  {language === 'ar' ? 'بداية العمل' : 'Work Start'}
                </label>
                <input
                  type="time"
                  className="wa-input"
                  value={bsWorkStart}
                  onChange={(e) => setBsWorkStart(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#64748b' }}>
                  {language === 'ar' ? 'نهاية العمل' : 'Work End'}
                </label>
                <input
                  type="time"
                  className="wa-input"
                  value={bsWorkEnd}
                  onChange={(e) => setBsWorkEnd(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', color: '#64748b' }}>
                  {language === 'ar' ? 'المنطقة الزمنية' : 'Timezone'}
                </label>
                <select
                  className="wa-input"
                  value={bsTimezone}
                  onChange={(e) => setBsTimezone(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="Asia/Riyadh">Asia/Riyadh (AST +3)</option>
                  <option value="Asia/Dubai">Asia/Dubai (GST +4)</option>
                  <option value="Asia/Kuwait">Asia/Kuwait (+3)</option>
                  <option value="Asia/Qatar">Asia/Qatar (+3)</option>
                  <option value="Asia/Bahrain">Asia/Bahrain (+3)</option>
                  <option value="Asia/Muscat">Asia/Muscat (+4)</option>
                  <option value="Africa/Cairo">Africa/Cairo (EET +2/+3)</option>
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
            </div>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#64748b' }}>
              {language === 'ar' 
                ? 'هذه الساعات تُستخدم لكل أيام العمل ما لم تُحدد ساعات مختلفة لأيام معينة أدناه.' 
                : 'These hours apply to all working days unless specific hours are set for individual days below.'}
            </p>
          </div>

          {/* Per-day hours override */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
              {language === 'ar' ? 'ساعات مخصصة لأيام محددة' : 'Custom Hours for Specific Days'}
            </label>
            <p style={{ margin: '0 0 12px 0', fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
              {language === 'ar' 
                ? 'اختر أيام محددة لتعيين ساعات عمل مختلفة (مثال: الجمعة من 14:00 إلى 17:00)' 
                : 'Set different working hours for specific days (e.g., Friday 14:00-17:00)'}
            </p>

            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { day: 0, arLabel: 'الأحد', enLabel: 'Sunday' },
                { day: 1, arLabel: 'الاثنين', enLabel: 'Monday' },
                { day: 2, arLabel: 'الثلاثاء', enLabel: 'Tuesday' },
                { day: 3, arLabel: 'الأربعاء', enLabel: 'Wednesday' },
                { day: 4, arLabel: 'الخميس', enLabel: 'Thursday' },
                { day: 5, arLabel: 'الجمعة', enLabel: 'Friday' },
                { day: 6, arLabel: 'السبت', enLabel: 'Saturday' }
              ].filter(({ day }) => bsWorkingDays.includes(day)).map(({ day, arLabel, enLabel }) => {
                const hasCustomHours = !!bsDayHours[day];
                const dayHours = getDayHours(day);
                const isDefault = !hasCustomHours;
                
                return (
                  <div key={day} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px', 
                    padding: '12px', 
                    borderRadius: '8px', 
                    border: `1px solid ${hasCustomHours ? '#f97316' : '#e2e8f0'}`,
                    background: hasCustomHours ? '#fff7ed' : '#f8fafc'
                  }}>
                    {/* Day name */}
                    <div style={{ minWidth: '90px', fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                      {language === 'ar' ? arLabel : enLabel}
                    </div>

                    {/* Time inputs or default display */}
                    {hasCustomHours ? (
                      <>
                        <input
                          type="time"
                          value={dayHours.start}
                          onChange={(e) => setDayHours(day, e.target.value, dayHours.end)}
                          style={{ ...inputStyle, width: '90px' }}
                        />
                        <span style={{ color: '#64748b', fontSize: '12px' }}>—</span>
                        <input
                          type="time"
                          value={dayHours.end}
                          onChange={(e) => setDayHours(day, dayHours.start, e.target.value)}
                          style={{ ...inputStyle, width: '90px' }}
                        />
                        <button
                          type="button"
                          onClick={() => clearDayHours(day)}
                          style={{ 
                            padding: '4px 8px', 
                            borderRadius: '6px', 
                            border: '1px solid #fecaca', 
                            background: '#fef2f2', 
                            color: '#dc2626', 
                            fontSize: '11px', 
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                        >
                          {language === 'ar' ? 'استخدام الافتراضي' : 'Use default'}
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ 
                          flex: 1, 
                          color: '#64748b', 
                          fontSize: '12px', 
                          fontStyle: 'italic',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          {language === 'ar' ? 'الساعات الافتراضية:' : 'Default hours:'} 
                          <span style={{ fontWeight: 600, color: '#334155' }}>
                            {bsWorkStart} — {bsWorkEnd}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setDayHours(day, bsWorkStart, bsWorkEnd)}
                          style={{ 
                            padding: '4px 8px', 
                            borderRadius: '6px', 
                            border: '1px solid #d1fae5', 
                            background: '#f0fdf4', 
                            color: '#059669', 
                            fontSize: '11px', 
                            cursor: 'pointer',
                            fontWeight: 600
                          }}
                        >
                          {language === 'ar' ? '+ تخصيص' : '+ Customize'}
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Summary of custom hours */}
            {Object.keys(bsDayHours).length > 0 && (
              <div style={{ 
                marginTop: '12px', 
                padding: '10px 12px', 
                borderRadius: '8px', 
                background: '#eff6ff', 
                border: '1px solid #bfdbfe',
                fontSize: '12px',
                color: '#1e40af'
              }}>
                <strong>{language === 'ar' ? 'ملخص الساعات المخصصة:' : 'Custom hours summary:'}</strong>
                {' '}
                {Object.entries(bsDayHours).map(([day, hours], index) => {
                  const dayNames = { 
                    0: language === 'ar' ? 'الأحد' : 'Sun', 
                    1: language === 'ar' ? 'الاثنين' : 'Mon', 
                    2: language === 'ar' ? 'الثلاثاء' : 'Tue',
                    3: language === 'ar' ? 'الأربعاء' : 'Wed',
                    4: language === 'ar' ? 'الخميس' : 'Thu',
                    5: language === 'ar' ? 'الجمعة' : 'Fri',
                    6: language === 'ar' ? 'السبت' : 'Sat'
                  };
                  return (
                    <span key={day}>
                      {index > 0 && ', '}
                      {dayNames[day]} {hours.start}–{hours.end}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Holidays */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontSize: '13px', fontWeight: 700, color: '#334155' }}>
              {language === 'ar' ? 'العطلات الرسمية' : 'Public Holidays'}
            </label>

            {bsHolidays.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '10px' }}>
                {language === 'ar' ? 'لا توجد عطلات مضافة بعد.' : 'No holidays added yet.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {bsHolidays.map((h) => (
                  <span key={h.date} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: '#fef3c7', border: '1px solid #fde68a',
                    borderRadius: '20px', padding: '4px 10px', fontSize: '13px', color: '#92400e'
                  }}>
                    {h.date}{h.label && h.label !== h.date ? ` — ${h.label}` : ''}
                    <button
                      type="button"
                      onClick={() => removeHoliday(h.date)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b45309', fontWeight: 700, padding: '0 2px', lineHeight: 1 }}
                    >×</button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  {language === 'ar' ? 'التاريخ' : 'Date'}
                </label>
                <DualCalendarPicker
                  value={bsNewHolidayDate}
                  onChange={(v) => setBsNewHolidayDate(v)}
                  language={language}
                  placeholder={language === 'ar' ? 'اختر تاريخاً' : 'Pick a date'}
                  style={{ fontSize: '13px' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>
                  {language === 'ar' ? 'الاسم (اختياري)' : 'Name (optional)'}
                </label>
                <input
                  type="text"
                  className="wa-input"
                  value={bsNewHolidayLabel}
                  onChange={(e) => setBsNewHolidayLabel(e.target.value)}
                  placeholder={language === 'ar' ? 'مثال: عيد الأضحى' : 'e.g. National Day'}
                  onKeyDown={(e) => { if (e.key === 'Enter') addHoliday(); }}
                  style={{ fontSize: '13px' }}
                />
              </div>
              <button
                type="button"
                className="wa-btn wa-btn-secondary"
                onClick={addHoliday}
                disabled={!bsNewHolidayDate}
                style={{ fontSize: '13px', whiteSpace: 'nowrap' }}
              >
                {language === 'ar' ? '+ إضافة' : '+ Add'}
              </button>
            </div>
          </div>

          {/* Info banner */}
          <div style={{ marginBottom: '14px', fontSize: '12px', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', padding: '10px 12px' }}>
            {language === 'ar'
              ? 'هذه الإعدادات تُستخدم الآن في احتساب أداء الفريق (متوسط وقت الرد خلال ساعات العمل). كما ستُستخدم لاحقاً للردود التلقائية خارج ساعات العمل.'
              : 'These settings are now used for team performance calculations (average response time during business hours). They will also power automated after-hours replies in future updates.'}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              className="wa-btn wa-btn-primary"
              onClick={handleSaveBusinessSettings}
              disabled={bsSaving}
            >
              {bsSaving
                ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                : (language === 'ar' ? 'حفظ الإعدادات' : 'Save settings')}
            </button>
            <button
              type="button"
              className="wa-btn wa-btn-secondary"
              onClick={loadBusinessSettings}
              disabled={bsSaving}
            >
              {language === 'ar' ? 'إعادة تحميل' : 'Reload'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── WhatsApp composite tab ───────────────────────────────────────────────────
const WhatsAppTab = ({ language, userRole }) => (
  <div>
    <WhatsAppSettings language={language} readOnly={false} userRole={userRole} />

    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
        <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FaLockOpen style={{ color: '#fff', fontSize: '14px' }} />
        </div>
        <h3 style={{ margin: 0, color: '#0f172a', fontSize: '16px', fontWeight: 700 }}>
          {language === 'ar' ? 'صلاحيات الوصول للأرقام' : 'Number Access Permissions'}
        </h3>
      </div>
      <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '13.5px', lineHeight: 1.6 }}>
        {language === 'ar'
          ? 'حدد الفرق التي يمكنها الوصول لكل رقم واتساب بشكل افتراضي. يمكن تعديل الصلاحيات الفردية من إعدادات الموظف.'
          : 'Configure which teams can access each WhatsApp number by default. Individual permissions can be customized in employee settings.'}
      </p>
      <NumberAccessSection language={language} />
    </div>
  </div>
);

// ─── Departments tab ──────────────────────────────────────────────────────────
const DepartmentsTab = ({ language }) => {
  const toast = useToast();
  const [custom, setCustom] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', label_ar: '', description: '', color: '#6b7280' });
  const [addForm, setAddForm] = useState({ label_ar: '', description: '', color: '#6b7280' });
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getOrgDepartments();
      setCustom(Array.isArray(res.data?.departments) ? res.data.departments : []);
    } catch { toast.error(language === 'ar' ? 'تعذر تحميل الأقسام' : 'Failed to load departments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    const arabicName = addForm.label_ar.trim();
    if (!arabicName) { toast.error(language === 'ar' ? 'اسم الفريق مطلوب' : 'Team name is required'); return; }
    
    // Validate name
    const validation = validateTeamName(arabicName, custom, language);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }
    
    const v = generateTeamKey(arabicName);
    const englishLabel = transliterateArabic(arabicName).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || arabicName;
    
    try {
      setSaving(true);
      await createOrgDepartment({ 
        value: v, 
        label: englishLabel, 
        label_ar: arabicName, 
        description: addForm.description.trim() || null,
        color: addForm.color 
      });
      setAddForm({ label_ar: '', description: '', color: '#6b7280' });
      setAdding(false);
      await load();
      toast.success(language === 'ar' ? 'تم إضافة القسم' : 'Department added');
    } catch (e) {
      toast.error(e?.response?.data?.error || (language === 'ar' ? 'تعذر الإضافة' : 'Failed to add'));
    } finally { setSaving(false); }
  };

  const handleEdit = async (id) => {
    try {
      setSaving(true);
      await updateOrgDepartment(id, { 
        label: editForm.label, 
        label_ar: editForm.label_ar || null, 
        description: editForm.description.trim() || null,
        color: editForm.color 
      });
      setEditingId(null);
      await load();
      toast.success(language === 'ar' ? 'تم الحفظ' : 'Saved');
    } catch { toast.error(language === 'ar' ? 'تعذر الحفظ' : 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, label) => {
    if (!window.confirm(language === 'ar' ? `حذف قسم "${label}"؟` : `Delete "${label}"?`)) return;
    try {
      await deleteOrgDepartment(id);
      await load();
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
    } catch { toast.error(language === 'ar' ? 'تعذر الحذف' : 'Delete failed'); }
  };

  return (
    <div>
      {/* Global departments section */}
      <div style={sectionHeadStyle}>
        <FaSitemap />
        {language === 'ar' ? 'الفرق الأساسية' : 'Standard Teams'}
      </div>
      
      <div style={cardStyle}>
        <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '0.875rem', lineHeight: 1.6 }}>
          {language === 'ar' 
            ? 'الفرق المحددة مسبقاً للقطاع العقاري — غير قابلة للتعديل'
            : 'Pre-defined teams for real estate sector — cannot be modified'}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {GLOBAL_DEPT_VALUES.map(v => (
            <span key={v} style={{ 
              padding: '6px 12px', 
              borderRadius: '20px', 
              background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)', 
              border: '1px solid #cbd5e1',
              color: '#475569', 
              fontSize: '0.8rem', 
              fontWeight: 600 
            }}>
              {language === 'ar' ? (GLOBAL_DEPTS_AR[v] || v) : v.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          ))}
        </div>
      </div>

      {/* Custom teams section */}
      <div style={sectionHeadStyle}>
        <FaPlus />
        {language === 'ar' ? 'فرق مخصصة' : 'Custom Teams'}
      </div>
      
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
            {language === 'ar' ? 'فرق إضافية خاصة بمؤسستك' : 'Additional teams specific to your organization'}
          </p>
          <button onClick={() => setAdding(v => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', border: 'none', background: adding ? '#fee2e2' : 'linear-gradient(135deg,#f97316,#ea580c)', color: adding ? '#b91c1c' : '#fff', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>
            {adding ? <><FaTimes /> {language === 'ar' ? 'إلغاء' : 'Cancel'}</> : <><FaPlus /> {language === 'ar' ? 'إضافة فريق' : 'Add Team'}</>}
          </button>
        </div>

      {adding && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '14px', padding: '16px', borderRadius: '10px', border: '1px dashed #f97316', background: '#fffbeb' }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
            {language === 'ar'
              ? 'يُنشأ المعرّف الداخلي تلقائياً من الاسم العربي (مثل: علاقات مميزة → alaqat_mumayaza) لاستخدامه في النظام.'
              : 'The internal key is generated automatically from the Arabic name (e.g. علاقات مميزة → alaqat_mumayaza) for system use.'}
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <input 
              placeholder={language === 'ar' ? 'اسم الفريق (مطلوب)' : 'Team name (required)'} 
              value={addForm.label_ar}
              onChange={e => setAddForm(p => ({ ...p, label_ar: e.target.value }))} 
              style={{ ...inputStyle, borderColor: addForm.label_ar.trim() ? '#cbd5e1' : '#fbbf24' }} 
              required
            />
            <textarea 
              placeholder={language === 'ar' ? 'وصف الفريق (اختياري)' : 'Team description (optional)'} 
              value={addForm.description}
              onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))} 
              rows={1}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '32px' }}
            />
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.76rem', color: '#64748b' }}>{language === 'ar' ? 'لون' : 'Color'}</span>
              <input type="color" value={addForm.color} onChange={e => setAddForm(p => ({ ...p, color: e.target.value }))}
                style={{ width: '34px', height: '28px', border: 'none', borderRadius: '6px', cursor: 'pointer' }} />
            </div>
            <button type="button" onClick={handleAdd} disabled={saving}
              style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: '#ea580c', color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
              {saving ? '…' : (language === 'ar' ? 'إضافة' : 'Add')}
            </button>
          </div>
        </div>
      )}

      {loading
        ? <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{language === 'ar' ? 'جاري التحميل...' : 'Loading…'}</p>
        : custom.length === 0
          ? <p style={{ color: '#94a3b8', fontSize: '0.875rem', padding: '16px', textAlign: 'center', fontStyle: 'italic' }}>{language === 'ar' ? 'لا توجد فرق مخصصة بعد.' : 'No custom teams yet.'}</p>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {custom.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e7e5e4', background: '#fafaf9' }}>
                  <span style={{ width: '11px', height: '11px', borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                  {editingId === d.id ? (
                    <>
                      <input value={editForm.label} onChange={e => setEditForm(p => ({ ...p, label: e.target.value }))} style={{ ...inputStyle, flex: 1 }} placeholder={language === 'ar' ? 'الاسم (إنجليزي)' : 'Label (English)'} />
                      <input value={editForm.label_ar} onChange={e => setEditForm(p => ({ ...p, label_ar: e.target.value }))} style={{ ...inputStyle, flex: 1 }} placeholder={language === 'ar' ? 'الاسم (عربي)' : 'Label (Arabic)'} />
                      <input type="color" value={editForm.color} onChange={e => setEditForm(p => ({ ...p, color: e.target.value }))}
                        style={{ width: '30px', height: '26px', border: 'none', borderRadius: '5px', cursor: 'pointer' }} />
                      <button onClick={() => handleEdit(d.id)} disabled={saving} style={iconBtnStyle('#16a34a')}><FaCheck /></button>
                      <button onClick={() => setEditingId(null)} style={iconBtnStyle('#9ca3af')}><FaTimes /></button>
                    </>
                  ) : (
                    <>
                      <span style={{ flex: 1, fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>
                        {d.label_ar || d.label}
                        {language === 'ar' && !d.label_ar && (
                          <span style={{ marginInlineStart: '6px', fontSize: '0.72rem', color: '#94a3b8', fontWeight: 400 }}>{d.label}</span>
                        )}
                      </span>
                      <span style={{ fontSize: '0.73rem', color: '#94a3b8', fontFamily: 'monospace' }}>{d.value}</span>
                      <button onClick={() => { setEditingId(d.id); setEditForm({ label: d.label, label_ar: d.label_ar || '', description: d.description || '', color: d.color || '#6b7280' }); }} style={iconBtnStyle('#3b82f6')}><FaEdit /></button>
                      <button onClick={() => handleDelete(d.id, d.label)} style={iconBtnStyle('#ef4444')}><FaTrash /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )
      }
      </div>
    </div>
  );
};

// ─── Settings hub ─────────────────────────────────────────────────────────────
const Settings = ({ language = 'ar', userRole = null, user = null, initialTab = 'organization', onUserUpdate = null }) => {
  const effectiveUserRole = userRole ?? user?.role ?? null;
  const [activeTab, setActiveTab] = useState(initialTab);
  const isRTL = language === 'ar';

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const handleBrandingSaved = (branding) => {
    if (!user || !onUserUpdate) return;
    onUserUpdate({
      ...user,
      business_name: branding?.business_name ?? user.business_name,
      company_logo_url: branding?.company_logo_url ?? null,
    });
  };

  return (
    <div style={{ display: 'flex', gap: '0', minHeight: '70vh', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{
        width: '200px', flexShrink: 0, background: '#fff',
        borderRight: isRTL ? 'none' : '1px solid #e2e8f0',
        borderLeft: isRTL ? '1px solid #e2e8f0' : 'none',
        padding: '20px 0',
      }}>
        <p style={{ margin: '0 0 10px', padding: '0 16px', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8' }}>
          {language === 'ar' ? 'الإعدادات' : 'Settings'}
        </p>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                width: '100%', padding: '10px 16px', border: 'none',
                background: active ? 'linear-gradient(90deg,#fff7ed,#fff)' : 'transparent',
                borderRight: !isRTL ? (active ? '3px solid #f97316' : '3px solid transparent') : 'none',
                borderLeft: isRTL ? (active ? '3px solid #f97316' : '3px solid transparent') : 'none',
                color: active ? '#ea580c' : '#64748b',
                fontWeight: active ? 800 : 600,
                fontSize: '0.88rem',
                cursor: 'pointer',
                textAlign: isRTL ? 'right' : 'left',
              }}
            >
              <Icon style={{ flexShrink: 0, fontSize: '14px' }} />
              {language === 'ar' ? tab.label_ar : tab.label}
            </button>
          );
        })}
      </div>

      {/* Content pane */}
      <div style={{ flex: 1, padding: '28px 32px', overflowY: 'auto' }}>
        {activeTab === 'whatsapp' && <WhatsAppTab language={language} userRole={effectiveUserRole} />}
        {activeTab === 'organization' && (
          <>
            <OrgBrandingTab language={language} onBrandingSaved={handleBrandingSaved} />
            <BusinessHoursTab language={language} />
          </>
        )}
        {activeTab === 'intake' && <IntakeTab language={language} />}
        {activeTab === 'departments' && <DepartmentsTab language={language} />}
      </div>
    </div>
  );
};

export default Settings;
